/* Forge — the strategy-building suite.
 *
 * Four lanes onto ONE engine. The lane you pick is about how you like to work,
 * not about what you are allowed to run: every lane terminates in an ordinary
 * strategy version, so backtest, paper and live are identical downstream and no
 * lane is a shortcut past the four-eyes live approval or the risk gate.
 *
 *   Classic  assemble blocks by hand — the existing rule-graph builder
 *   Copilot  describe it, review what was understood, accept or discard
 *   Pro      send written rules to the desk and track the build
 *   Python   register your own code artefact against the engine toolchain
 *
 * The Copilot lane is the one that needed the most care. A generated strategy
 * that a trader cannot audit against the sentence that produced it is not
 * something to point money at, so this screen will not let a draft be accepted
 * without showing, condition by condition, which phrase produced it and what
 * the drafter could not read.
 */
import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { useStore } from '@/store'
import { Panel } from '@/components/Panel'
import { Empty } from '@/components/viz'
import { StrategyBuilder } from '@/panels/StrategyBuilder'
import { fmtDateTime } from '@/lib/format'
import { FORGE_LANES, type ForgeLaneId } from '@/lib/products'
import type {
  CopilotDraftResponse, ForgeBuildRequest, RuleGraph,
} from '@/types'

export function Forge({ initialLane = 'CLASSIC' }: { initialLane?: ForgeLaneId }) {
  const [lane, setLane] = useState<ForgeLaneId>(initialLane)

  // The shell can re-open Forge on a different lane (the top bar's "Create
  // Strategies Using AI" lands on Copilot) without remounting the screen.
  useEffect(() => { setLane(initialLane) }, [initialLane])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 'var(--sp-2)',
      }}>
        {FORGE_LANES.map((l) => {
          const on = lane === l.id
          return (
            <button
              key={l.id}
              onClick={() => setLane(l.id)}
              aria-pressed={on}
              style={{
                textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                background: on ? 'var(--accent-bg)' : 'var(--bg-panel)',
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-lg)', padding: 'var(--sp-3) var(--sp-4)',
                boxShadow: on ? 'none' : 'var(--shadow-sm)',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}
            >
              <span style={{
                fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: 'var(--accent)',
              }}>
                {l.kicker}
              </span>
              <span style={{
                fontSize: 'var(--fs-lg)', fontWeight: 800,
                color: 'var(--text-primary)',
              }}>
                {l.label}
              </span>
              <span style={{
                fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
                lineHeight: 1.5,
              }}>
                {l.blurb}
              </span>
            </button>
          )
        })}
      </div>

      {lane === 'CLASSIC' && <StrategyBuilder />}
      {lane === 'COPILOT' && <CopilotLane />}
      {lane === 'PRO' && <ProBuildLane />}
      {lane === 'PYTHON' && <PythonLane />}
    </div>
  )
}

// ===========================================================================
// Copilot
// ===========================================================================
function CopilotLane() {
  const can = useStore((s) => s.can)
  const canDraft = can('forge:copilot')
  const canAccept = can('strategy:edit')

  const [prompt, setPrompt] = useState('')
  const [busy, setBusy] = useState(false)
  const [draft, setDraft] = useState<CopilotDraftResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [accepted, setAccepted] = useState<string | null>(null)

  const run = async () => {
    setBusy(true); setError(null); setDraft(null); setAccepted(null)
    try {
      const d = await api.copilotDraft(prompt)
      setDraft(d)
      setName(prompt.slice(0, 60))
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  const accept = async () => {
    if (!draft) return
    setBusy(true); setError(null)
    try {
      const r = await api.copilotAccept(draft.draft_id, { code, name })
      setAccepted(r.note)
      setDraft(null)
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!canDraft) {
    return <Panel title="Copilot"><Empty>
      Your role does not include forge:copilot.
    </Empty></Panel>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <Panel title="Describe the strategy">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          placeholder="Buy SBIN when RSI goes below 30 and price crosses above the 20 SMA. Sell when RSI rises above 70. Stop loss 1.5%, target 3%, quantity 50."
          style={{
            width: '100%', boxSizing: 'border-box', resize: 'vertical',
            background: 'var(--bg-input)', color: 'var(--text-primary)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
            fontSize: 'var(--fs-sm)', fontFamily: 'inherit', lineHeight: 1.6,
          }}
        />
        <div style={{
          display: 'flex', gap: 'var(--sp-3)', alignItems: 'center',
          marginTop: 'var(--sp-3)', flexWrap: 'wrap',
        }}>
          <button
            onClick={() => void run()}
            disabled={busy || prompt.trim().length < 10}
            style={primaryButton(busy || prompt.trim().length < 10)}
          >
            {busy ? 'Drafting…' : 'Draft it'}
          </button>
          <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
            Name a symbol the engine serves, a condition to enter on, one to exit
            on, and your stop and target.
          </span>
        </div>
      </Panel>

      {error && <ErrorBox>{error}</ErrorBox>}
      {accepted && (
        <div style={{
          background: 'var(--ok-bg)', border: '1px solid var(--ok)',
          borderRadius: 'var(--radius-md)', padding: 'var(--sp-4)',
          color: 'var(--ok)', fontSize: 'var(--fs-sm)', lineHeight: 1.6,
        }}>
          {accepted} Open the <b>Classic Build</b> lane to backtest it.
        </div>
      )}

      {draft && (
        <>
          {/* The drafter names itself. A trader is entitled to know whether the
              thing that wrote their strategy was a language model or a phrase
              parser, and this is not printed in small grey text at the bottom. */}
          <div style={{
            background: 'var(--accent-soft)', border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
            fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}>
            <b style={{ color: 'var(--accent)' }}>Drafted by:</b>{' '}
            {draft.generator}. Nothing has been saved as a strategy yet — review
            every line below, then accept or discard.
          </div>

          <Panel title="What Copilot understood">
            <table className="qx">
              <thead>
                <tr><th style={{ width: 90 }}>Section</th><th>Your words</th>
                    <th>Became</th></tr>
              </thead>
              <tbody>
                {draft.interpretation.map((i, n) => (
                  <tr key={n}>
                    <td style={{
                      fontWeight: 800, textTransform: 'uppercase',
                      color: 'var(--accent)', fontSize: 'var(--fs-2xs)',
                    }}>
                      {i.section}
                    </td>
                    <td style={{ whiteSpace: 'normal', color: 'var(--text-secondary)' }}>
                      “{i.phrase}”
                    </td>
                    <td className="num" style={{ fontWeight: 700 }}>{i.understood}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          {draft.unresolved.length > 0 && (
            <Panel title="What it could not read">
              <ul style={{
                margin: 0, paddingLeft: 'var(--sp-5)',
                fontSize: 'var(--fs-sm)', lineHeight: 1.7,
                color: 'var(--text-secondary)',
              }}>
                {draft.unresolved.map((u, n) => (
                  <li key={n} style={{ marginBottom: 4 }}>{u}</li>
                ))}
              </ul>
              <p style={{
                fontSize: 'var(--fs-xs)', color: 'var(--warn)',
                marginTop: 'var(--sp-3)', fontWeight: 700, lineHeight: 1.6,
              }}>
                Each of these applied a default instead of your intent. Accepting
                the draft accepts those defaults too.
              </p>
            </Panel>
          )}

          <Panel title="The rule graph as drafted">
            <GraphView graph={draft.rule_graph} />
          </Panel>

          <Panel title="Accept as a strategy">
            {!canAccept ? (
              <Empty>
                Accepting a draft creates an authored strategy and needs
                strategy:edit, which your role does not have. You can still draft
                and review here.
              </Empty>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap',
                            alignItems: 'flex-end' }}>
                <Field label="Code" hint="Letters, digits, _ and - only">
                  <input value={code} onChange={(e) => setCode(e.target.value)}
                         placeholder="RSI_SMA_SBIN" style={fieldInput} />
                </Field>
                <Field label="Name">
                  <input value={name} onChange={(e) => setName(e.target.value)}
                         style={{ ...fieldInput, minWidth: 280 }} />
                </Field>
                <button
                  onClick={() => void accept()}
                  disabled={busy || !/^[A-Za-z0-9_-]{2,50}$/.test(code) || !name.trim()}
                  style={primaryButton(
                    busy || !/^[A-Za-z0-9_-]{2,50}$/.test(code) || !name.trim())}
                >
                  Accept and create
                </button>
                <button
                  onClick={() => { void api.copilotDiscard(draft.draft_id); setDraft(null) }}
                  style={ghostButton}
                >
                  Discard
                </button>
              </div>
            )}
            <p style={{
              fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
              marginTop: 'var(--sp-3)', lineHeight: 1.6,
            }}>
              Accepting creates a DRAFT strategy. It is not approved for live
              trading and cannot be until it has a completed backtest and a
              second person approves it.
            </p>
          </Panel>
        </>
      )}

      <DraftHistory />
    </div>
  )
}

function DraftHistory() {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof api.copilotDrafts>>>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void api.copilotDrafts()
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoaded(true))
  }, [])

  if (!loaded) return null
  if (rows.length === 0) return null

  return (
    <Panel title="Earlier drafts">
      <div className="scroll-x">
        <table className="qx">
          <thead>
            <tr><th>Drafted</th><th>Description</th><th>Status</th>
                <th className="r">Unread items</th></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td>{fmtDateTime(d.created_at)}</td>
                <td style={{ whiteSpace: 'normal', maxWidth: 520,
                             color: 'var(--text-secondary)' }}>
                  {d.prompt}
                </td>
                <td style={{
                  fontWeight: 700,
                  color: d.status === 'ACCEPTED' ? 'var(--ok)'
                    : d.status === 'DISCARDED' ? 'var(--text-muted)' : 'var(--accent)',
                }}>
                  {d.status}
                </td>
                <td className="num r" style={{
                  color: d.unresolved.length ? 'var(--warn)' : 'var(--text-muted)',
                }}>
                  {d.unresolved.length}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  )
}

// ===========================================================================
// Pro Build
// ===========================================================================
function ProBuildLane() {
  const [title, setTitle] = useState('')
  const [rules, setRules] = useState('')
  const [symbols, setSymbols] = useState('')
  const [capital, setCapital] = useState('')
  const [riskNotes, setRiskNotes] = useState('')
  const [rows, setRows] = useState<ForgeBuildRequest[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setRows(await api.buildRequests()) } catch { setRows([]) }
  }, [])
  useEffect(() => { void load() }, [load])

  const submit = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api.createBuildRequest({
        title,
        rules_text: rules,
        symbols: symbols.split(',').map((s) => s.trim()).filter(Boolean),
        target_lane: 'CLASSIC',
        indicative_capital: capital || undefined,
        risk_notes: riskNotes || undefined,
      })
      setSent(r.note)
      setTitle(''); setRules(''); setSymbols(''); setCapital(''); setRiskNotes('')
      void load()
    } catch (e) {
      setError(e instanceof ApiError ? (e.detail ?? e.message) : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <Panel title="Send your rules to the desk">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <Field label="What should it do?">
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="Intraday momentum on banking names"
                   style={{ ...fieldInput, width: '100%' }} />
          </Field>
          <Field label="The rules, in your own words"
                 hint="Entry, exit, position sizing and anything that must never happen.">
            <textarea
              value={rules} onChange={(e) => setRules(e.target.value)} rows={7}
              placeholder={'Enter long when …\nExit when …\nNever hold overnight.\nMax 2 open positions.'}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                background: 'var(--bg-input)', color: 'var(--text-primary)',
                border: '1px solid var(--border-strong)',
                borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
                fontSize: 'var(--fs-sm)', fontFamily: 'inherit', lineHeight: 1.6,
              }}
            />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
            <Field label="Instruments" hint="Comma separated">
              <input value={symbols} onChange={(e) => setSymbols(e.target.value)}
                     placeholder="SBIN, HDFCBANK" style={fieldInput} />
            </Field>
            <Field label="Indicative capital" hint="Sizing only — not a commitment">
              <input value={capital} onChange={(e) => setCapital(e.target.value)}
                     inputMode="decimal" placeholder="500000" style={fieldInput} />
            </Field>
          </div>
          <Field label="Risk notes" hint="Limits the build must respect">
            <input value={riskNotes} onChange={(e) => setRiskNotes(e.target.value)}
                   placeholder="No leverage. Hard daily loss cap of 1%."
                   style={{ ...fieldInput, width: '100%' }} />
          </Field>

          {error && <ErrorBox>{error}</ErrorBox>}
          {sent && (
            <div style={{
              background: 'var(--ok-bg)', border: '1px solid var(--ok)',
              borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
              color: 'var(--ok)', fontSize: 'var(--fs-sm)', lineHeight: 1.6,
            }}>
              {sent}
            </div>
          )}

          <button
            onClick={() => void submit()}
            disabled={busy || title.trim().length < 3 || rules.trim().length < 20}
            style={{
              ...primaryButton(busy || title.trim().length < 3 || rules.trim().length < 20),
              alignSelf: 'flex-start',
            }}
          >
            {busy ? 'Sending…' : 'Send to the desk'}
          </button>
        </div>
      </Panel>

      <Panel title="My build requests">
        {rows.length === 0 ? (
          <Empty>Nothing sent yet.</Empty>
        ) : (
          <div className="scroll-x">
            <table className="qx">
              <thead>
                <tr><th>Sent</th><th>Title</th><th>Status</th><th>Desk notes</th>
                    <th /></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDateTime(r.created_at)}</td>
                    <td style={{ fontWeight: 700 }}>{r.title}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td style={{ whiteSpace: 'normal', maxWidth: 420,
                                 color: 'var(--text-secondary)' }}>
                      {r.desk_notes ?? '—'}
                    </td>
                    <td className="r">
                      {!['DELIVERED', 'REJECTED', 'WITHDRAWN'].includes(r.status) && (
                        <button
                          onClick={() => { void api.withdrawBuildRequest(r.id).then(load) }}
                          style={{
                            background: 'transparent', border: '1px solid var(--border-strong)',
                            color: 'var(--text-secondary)', borderRadius: 'var(--radius-sm)',
                            padding: '2px var(--sp-2)', cursor: 'pointer',
                            fontSize: 'var(--fs-2xs)', fontWeight: 700,
                            fontFamily: 'inherit',
                          }}
                        >
                          Withdraw
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p style={{
          fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
          marginTop: 'var(--sp-3)', lineHeight: 1.6,
        }}>
          A delivered strategy arrives as a <b>draft</b> in your strategy list.
          Delivery is not approval: you still backtest it, and a second person
          still has to approve it before it can trade live.
        </p>
      </Panel>
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'DELIVERED' ? 'var(--ok)' :
    status === 'REJECTED' ? 'var(--danger)' :
    status === 'WITHDRAWN' ? 'var(--text-muted)' : 'var(--accent)'
  return (
    <span style={{
      color: tone, background: 'var(--bg-hover)', border: `1px solid ${tone}33`,
      borderRadius: 'var(--radius-pill)', padding: '2px 8px',
      fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.05em',
    }}>
      {status}
    </span>
  )
}

// ===========================================================================
// Python
// ===========================================================================
function PythonLane() {
  const [blocks, setBlocks] =
    useState<Awaited<ReturnType<typeof api.forgeBlocks>> | null>(null)
  useEffect(() => { void api.forgeBlocks().then(setBlocks).catch(() => setBlocks(null)) }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <Panel title="Python Build">
        <div style={{ fontSize: 'var(--fs-sm)', lineHeight: 1.7,
                      color: 'var(--text-secondary)', maxWidth: 820 }}>
          <p style={{ marginBottom: 'var(--sp-3)' }}>
            Write the logic yourself — your own signals, data sources and models —
            and run it on the same execution, risk and reporting stack as every
            other lane.
          </p>
          <p style={{ marginBottom: 'var(--sp-3)' }}>
            The honest limitation, stated up front rather than discovered at
            deploy time: a Python strategy is <b>built and loaded through the
            engine's own toolchain</b>, not pushed from this browser. You
            register the strategy and pin its artefact reference here (a git SHA
            or build id); the engine loads that artefact. What the API will not
            do is pretend a browser can hand arbitrary code to a live trading
            engine.
          </p>
          <ol style={{ paddingLeft: 'var(--sp-5)', marginBottom: 0 }}>
            <li style={{ marginBottom: 6 }}>
              Create the strategy in the <b>Classic Build</b> lane with language{' '}
              <code className="code">PYTHON</code>, and save a version whose
              source reference is your build artefact.
            </li>
            <li style={{ marginBottom: 6 }}>
              Backtest it here like any other strategy — historical runs execute
              against stored data and do not need the engine toolchain.
            </li>
            <li>
              Deploy through the engine's configuration once the artefact is
              published to it. Live promotion still needs a completed backtest
              and a second approver.
            </li>
          </ol>
        </div>
      </Panel>

      <Panel title="The vocabulary the engine implements">
        {!blocks ? <Empty>Loading the block catalogue…</Empty> : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--sp-3)',
            }}>
              {Object.entries(
                blocks.operands.reduce<Record<string, typeof blocks.operands>>(
                  (acc, o) => {
                    (acc[o.group] ??= []).push(o)
                    return acc
                  }, {}),
              ).map(([group, items]) => (
                <div key={group}>
                  <h4 className="qx-card-title" style={{ marginBottom: 6 }}>{group}</h4>
                  <ul style={{ listStyle: 'none', fontSize: 'var(--fs-xs)',
                               lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                    {items.map((o) => (
                      <li key={o.code}>
                        <span className="code" style={{ color: 'var(--text-primary)' }}>
                          {o.code}
                        </span>{' '}— {o.label}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p style={{
              fontSize: 'var(--fs-xs)', color: 'var(--text-muted)',
              marginTop: 'var(--sp-4)', lineHeight: 1.6,
            }}>
              {blocks.note}
            </p>
          </>
        )}
      </Panel>
    </div>
  )
}

// ===========================================================================
// Shared bits
// ===========================================================================
function GraphView({ graph }: { graph: RuleGraph }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      <RuleSetView label="Entry" set={graph.entry} />
      <RuleSetView label="Exit" set={graph.exit} />
      <div>
        <h4 className="qx-card-title" style={{ marginBottom: 6 }}>Risk</h4>
        <div className="num" style={{ fontSize: 'var(--fs-sm)' }}>
          size {graph.risk.position_size} · max {graph.risk.max_position} ·
          stop {graph.risk.stop_loss_pct}% · target {graph.risk.take_profit_pct}% ·
          trailing {graph.risk.trailing_stop_pct}%
        </div>
      </div>
      <div>
        <h4 className="qx-card-title" style={{ marginBottom: 6 }}>Instruments</h4>
        <div className="num" style={{ fontSize: 'var(--fs-sm)' }}>
          {graph.instruments.length
            ? `engine ids ${graph.instruments.join(', ')}`
            : 'none — this draft cannot be deployed as it stands'}
        </div>
      </div>
    </div>
  )
}

function RuleSetView({ label, set }: { label: string; set: RuleGraph['entry'] }) {
  return (
    <div>
      <h4 className="qx-card-title" style={{ marginBottom: 6 }}>
        {label} — all conditions joined with {set.combinator}
      </h4>
      <ul style={{ listStyle: 'none', display: 'grid', gap: 4 }}>
        {set.conditions.map((c, i) => (
          <li key={i} className="num" style={{
            fontSize: 'var(--fs-sm)', background: 'var(--bg-subtle)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            padding: '4px var(--sp-3)',
          }}>
            {c.left} {c.op} {c.right ?? c.value}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: '0 1 auto' }}>
      <span style={{
        fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: 'var(--text-muted)',
      }}>
        {label}
      </span>
      {children}
      {hint && (
        <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
          {hint}
        </span>
      )}
    </label>
  )
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--danger-bg)', border: '1px solid var(--danger)',
      borderRadius: 'var(--radius-md)', padding: 'var(--sp-3)',
      color: 'var(--danger)', fontSize: 'var(--fs-sm)', lineHeight: 1.5,
    }}>
      {children}
    </div>
  )
}

const fieldInput: React.CSSProperties = {
  background: 'var(--bg-input)', color: 'var(--text-primary)',
  border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
  padding: '7px var(--sp-3)', fontSize: 'var(--fs-sm)', fontFamily: 'inherit',
  minWidth: 180,
}

function primaryButton(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'var(--bg-active)' : 'var(--accent)',
    color: disabled ? 'var(--text-muted)' : 'var(--text-inverse)',
    border: 'none', borderRadius: 'var(--radius-md)',
    padding: '9px var(--sp-5)', fontSize: 'var(--fs-sm)', fontWeight: 800,
    fontFamily: 'inherit', cursor: disabled ? 'not-allowed' : 'pointer',
  }
}

const ghostButton: React.CSSProperties = {
  background: 'transparent', color: 'var(--text-secondary)',
  border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
  padding: '9px var(--sp-4)', fontSize: 'var(--fs-sm)', fontWeight: 700,
  fontFamily: 'inherit', cursor: 'pointer',
}
